import React from "react";
import { ClipLoader } from "react-spinners";
import { compose, lifecycle, withState } from "recompose";
import styled from "styled-components";
import { Elements } from "react-stripe-elements";
import StripeCheckoutHandler from "../common/Checkout/StripeCheckoutHandler";
import PaymentRequestButton from "../common/Checkout/PaymentRequestForm";
import lockSVG from "../../../images/lock.svg";
import Preview from "../common/Preview";
import { ModalTitle } from "../common/Modal";
import UnlockTopNineInfo from "../TopNineDetail/UnlockTopNineInfo";
import Error from "../common/Error";
import { Cta } from "../common/Button";
import SupportUs from "../TopNineDetail/SupportUs";

const withCanContinue = withState("canContinue", "setCanContinue", false);
const TEMPLATE_VIDEO_HEIGHT = 400;

const PreviewStep = props => {
  const {
    canContinue,
    setCanContinue,
    onCreateVideoOrder,
    createVideoOrderLoading,
    createVideoOrderError,
    getCurrentComposition,
  } = props;
  const composition = getCurrentComposition();
  return (
    <Layout>
      <StripeCheckoutHandler
        name={"Top Nine Video - Support us"}
        description={"Thank you for your support!"}
        amount={props.videoPrice || 299}
        onTokenSuccess={props.onTokenSuccess}
      >
        {({ openStripe }) => (
          <React.Fragment>
            <ModalTitle style={{ marginBottom: "10px" }}>Your Top Nine Video</ModalTitle>
            <Subtitle>Paying for your video helps us maintain and support Top Nine.</Subtitle>
            <br />
            <SubtitleBold>🤗 Thank you for your support! 🤗</SubtitleBold>
            <LayoutBlock large>
              <PreviewContainer>
                <Preview onReady={() => setCanContinue(true)} composition={composition} />
                <DescriptionContainer>
                  <Description>
                    This is a<b> low resolution </b>preview.
                    <br />
                    Continue for the HD version!
                  </Description>
                </DescriptionContainer>
              </PreviewContainer>
            </LayoutBlock>
            <ButtonContainer withBottomMargin={!props.isVideoLocked || props.userPurchasesLoading}>
              {!props.isVideoLocked &&
                <Elements>
                  <PaymentRequestButton
                    onTokenSuccess={(token, method) => props.onTokenSuccess(token)}
                    amount={props.videoPrice || 299}
                  />
                </Elements>
              }
              {props.loadingPurchase ? (
                <LoadingButton>
                  <ClipLoader size={25} color={"white"} loading={true} />
                </LoadingButton>
              ) : (
                <Cta
                  disabled={!canContinue || createVideoOrderLoading || createVideoOrderError}
                  onClick={() => {
                    if (props.isVideoLocked) {
                      if (window.gtag) {
                        window.gtag("event", "preview_continue_stripe");
                        window.gtag("event", "checkout_progress");
                      }
                      openStripe();
                    } else {
                      props.handlePreviewOnClick();
                    }
                  }}
                  style={{ marginTop: 10, marginBottom: 10 }}
                >
                  Continue
                </Cta>
              )}
              {createVideoOrderError && <Error>{createVideoOrderError}</Error>}
              {props.isVideoLocked && !props.userPurchasesLoading && (
                <StripeCopy>
                  <Lock /> Secure payment by Stripe
                </StripeCopy>
              )}
            </ButtonContainer>
          </React.Fragment>
        )}
      </StripeCheckoutHandler>
      <InfoContainer>
        <SupportUs videoSupportUs={true} />
        <UnlockTopNineInfo
          videoSupportUs={true}
          userName={props.userName}
          userEmail={props.userEmail}
          price={props.videoPrice || 299}
          buttonText={"Continue"}
          onTokenSuccess={props.onTokenSuccess}
          setStatus={() => {}}
        />
      </InfoContainer>
    </Layout>
  );
};

const PreviewContainer = styled.div`
  position: relative;
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: center;
  flex-direction: column;
`;

const Layout = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  overflow-y: scroll;
`;

const LayoutBlock = styled.div`
  width: 100%;
  padding: 20px;
`;

const ButtonContainer = styled.div`
  width: 80%;
  margin-bottom: ${({ withBottomMargin }) => (withBottomMargin ? "20px" : 0)};
`;

const LoadingButton = styled.div`
  height: 40px;
  width: 40px;
  border-radius: 20px;
  background-color: #cdcdcd;
  margin: auto;
  justify-content: center;
  align-items: center;
  display: flex;
`;

const DescriptionContainer = styled.div`
  margin-top: 10px;
`;

const InfoContainer = styled.div`
  padding: 0px 30px 20px;
`;

const Description = styled.p`
  font-size: 12px;
  text-align: center;
  line-height: 15px;
  font-family: "Montserrat";
  font-weight: ${({ bold }) => (bold ? "500" : "normal")};
`;

const Subtitle = styled(Description)`
  font-size: 14px;
  line-height: 18px;
  max-width: 80%;
`;

const SubtitleBold = styled(Description)`
  font-size: 14px;
  font-weight: 900;
`;

const Lock = styled.img.attrs({
  src: lockSVG,
})`
  width: 14px;
  height: 16px;
  margin-right: 8px;
`;

const Text = styled.p`
  color: #6e788d;
  font-size: 15px !important;
  font-family: "Montserrat";
  font-weight: 500;
  text-align: left;
  @media (max-width: 640px) {
    font-size: 14px !important;
  }
  b {
    color: #f74255;
  }
`;

const StripeCopy = styled(Text)`
  display: flex;
  align-items: center;
  margin: auto;
  font-size: 14px;
  color: #4dc384;
  margin-top: 5px;
  text-align: center;
  margin-bottom: 20px !important;
  justify-content: center;
`;

const enhancer = compose(
  withCanContinue,
  lifecycle({
    componentDidMount() {
      if (window.gtag) {
        window.gtag("event", "page_view", { title: "Video Preview" });
      }
    },
  })
);

export default enhancer(PreviewStep);
